<template>
  <div class="row justify-content-center p-3">
    <div class="col-12 col-md-10 col-lg-8">
      <div class="card border-0 rounded-4 shadow-lg authentication-card position-relative overflow-hidden">

        <div :style="accentStyle" style="height: 4px;"></div>

        <button
          type="button"
          class="btn-close position-absolute top-0 end-0 m-3 shadow-none"
          aria-label="Fechar"
          :disabled="loading"
          @click.prevent="handleClose"
        ></button>

        <div class="card-body p-4 p-md-5">

          <!-- Cabeçalho -->
          <div class="text-center mb-4">
            <div
              class="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
              :class="iconCircleClass"
              style="width: 60px; height: 60px;"
            >
              <i :class="iconClass" class="fs-3"></i>
            </div>
            <h4 class="text-dark fw-bold">{{ headerTitle }}</h4>
            <p class="text-muted small">{{ headerSubtitle }}</p>
          </div>

          <!-- Indicador de passos -->
          <div class="d-flex align-items-center justify-content-center gap-2 mb-4">
            <span class="badge rounded-pill px-3 py-2" :class="step === 1 ? 'text-bg-warning' : 'bg-success'">
              1. Justificativa
            </span>
            <i class="fa-solid fa-chevron-right text-muted small"></i>
            <span class="badge rounded-pill px-3 py-2" :class="step >= 2 ? 'bg-success' : 'bg-secondary bg-opacity-25 text-secondary'">
              2. Autorização
            </span>
          </div>

          <!-- Alert de erro -->
          <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show small" role="alert">
            <i class="fa-solid fa-triangle-exclamation me-2"></i> {{ errorMessage }}
            <button type="button" class="btn-close" @click="errorMessage = ''"></button>
          </div>

          <!-- Alert de sucesso -->
          <div v-if="successMessage" class="alert alert-success small" role="alert">
            <i class="fa-solid fa-circle-check me-2"></i> {{ successMessage }}
          </div>

          <!-- PASSO 1: Justificativa -->
          <form v-if="step === 1" @submit.prevent="handleSolicitacao">
            <div class="mb-4">
              <label class="form-label text-secondary small fw-bold text-uppercase">
                Justificativa
              </label>
              <textarea
                v-model="justificativa"
                class="form-control form-control-lg rounded-3 border-warning border-2"
                rows="4"
                placeholder="Descreva o motivo pelo qual precisa executar esta operação..."
                required
                :disabled="loading"
                style="background-color: var(--surface-field); font-size: 1rem;"
              ></textarea>
            </div>

            <button
              class="btn btn-warning btn-lg w-100 rounded-3 fw-bold py-3 d-flex align-items-center justify-content-center"
              type="submit"
              :disabled="loading || !justificativa.trim()"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ loading ? 'Registrando...' : 'Registrar Solicitação' }}
            </button>
          </form>

          <!-- Aguardando aprovação remota -->
          <div v-else-if="step === 3">
            <div class="text-center py-3">
              <div class="spinner-border text-success mb-3" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Aguardando...</span>
              </div>
              <p class="fw-semibold text-dark mb-1">Aguardando aprovação do gestor</p>
              <p class="text-muted small mb-4">
                Os gestores foram notificados por e-mail e alerta no sistema.<br>
                O modal fechará automaticamente quando alguém liberar.
              </p>

              <div class="alert alert-light border small text-start" role="alert">
                <i class="fa-solid fa-circle-info me-2 text-primary"></i>
                O gestor pode acessar <strong>/gerencia/liberacoes</strong> e aprovar com sua própria senha.
              </div>

              <div class="border-top pt-3 mt-2">
                <p class="text-muted small mb-2">Gestor está presente fisicamente?</p>
                <button
                  type="button"
                  class="btn btn-outline-success rounded-3 fw-semibold"
                  @click="step = 2"
                >
                  <i class="fa-solid fa-user-check me-2"></i> Inserir credenciais manualmente
                </button>
              </div>
            </div>
          </div>

          <!-- Credenciais do gestor (presencial) -->
          <form v-else @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label class="form-label text-secondary small fw-bold text-uppercase">E-mail do Gestor</label>
              <input
                v-model="form.Email"
                type="email"
                class="form-control form-control-lg rounded-3 border-light-subtle bg-light-subtle fs-6"
                placeholder="gestor@empresa.com.br"
                required
                :disabled="loading"
              >
            </div>

            <div class="mb-4">
              <label class="form-label text-secondary small fw-bold text-uppercase">Senha de Autorização</label>
              <input
                v-model="form.Senha"
                type="password"
                class="form-control form-control-lg rounded-3 border-light-subtle bg-light-subtle fs-6"
                placeholder="••••••••••••"
                required
                :disabled="loading"
              >
            </div>

            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-3 fw-bold py-3 px-4"
                :disabled="loading"
                @click="step = 3"
              >
                <i class="fa-solid fa-arrow-left me-1"></i> Voltar
              </button>
              <button
                class="btn btn-success btn-lg flex-grow-1 rounded-3 fw-bold py-3 d-flex align-items-center justify-content-center"
                type="submit"
                :disabled="loading"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ loading ? 'Validando...' : 'Liberar Ação' }}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import AgendaService from '@/services/AgendaService.js';
import UsuarioService from '@/services/UsuarioService';
import unlockValidationModalStore from '@/stores/unlockValidationModalStore';
import { useToast } from '@/plugins/useToast';

const { showToast } = useToast();

const emit = defineEmits(['close', 'success']);

/** Passos possíveis: 
  1 = justificativa, 
  2 = credenciais presenciais,
  3 = aguardando remoto
*/
const step = ref(1);

const justificativa = ref('');
const form = reactive({ Email: '', Senha: '' });
const loading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// #region Cabeçalho dinâmico
const accentStyle = computed(() => {
  if (step.value === 1) return 'background-color: #ffc107';
  if (step.value === 3) return 'background-color: #0d6efd';
  return 'background-color: #198754';
});

const iconCircleClass = computed(() => {
  if (step.value === 1) return 'bg-warning bg-opacity-10';
  if (step.value === 3) return 'bg-primary bg-opacity-10';
  return 'bg-light';
});

const iconClass = computed(() => {
  console.log("calling icons ❌❌❌❌")
  if (step.value === 1) return 'fa-solid fa-triangle-exclamation';
  if (step.value === 3) return 'fa-solid fa-hourglass-half';
  return 'fa-solid fa-hand';
});

const headerTitle = computed(() => {
  if (step.value === 1) return 'Solicitar Liberação';
  if (step.value === 3) return 'Aguardando Aprovação';
  return 'Autorização do Gestor';
});
// #endregion

const headerSubtitle = computed(() => {
  if (step.value === 1) return 'O processo está bloqueado. Informe o motivo para o gestor avaliar a liberação.';
  if (step.value === 3) return 'Gestores foram notificados e podem aprovar da própria máquina.';
  return 'Um gestor deve autorizar esta operação com suas credenciais.';
});

const handleClose = () => {
  emit('close');
};

// Reset ao fechar
watch(() => unlockValidationModalStore.isOpen.value, (isOpen) => {
  if (!isOpen) {
    step.value = 1;
    justificativa.value = '';
    form.Email = '';
    form.Senha = '';
    errorMessage.value = '';
    successMessage.value = '';
  }
});

// Fecha o modal automaticamente quando o gestor libera remotamente (via MainNotification.vue)
watch(() => unlockValidationModalStore.liberado.value, (liberado) => {
  if (!liberado) return;
  const gestorNome = unlockValidationModalStore.liberadoPorNome.value;
  successMessage.value = `Processo liberado por ${gestorNome}. Você tem 3 minutos para executar a ação.`;
  setTimeout(() => emit('success'), 1200);
});

// — PASSO 1: registra solicitação —
const handleSolicitacao = async () => {
  const processoId = unlockValidationModalStore.processoId.value;
  if (!processoId) {
    step.value = 2;
    return;
  }

  errorMessage.value = '';
  loading.value = true;
  try {
    await AgendaService.solicitarLiberacao(processoId, justificativa.value);
    step.value = 3;
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? 'Não foi possível registrar a solicitação.';
  } finally {
    loading.value = false;
  }
};

// — PASSO 2: gestor autoriza presencialmente —
const handleSubmit = async () => {
  const processoId = unlockValidationModalStore.processoId.value;

  errorMessage.value = '';
  successMessage.value = '';
  loading.value = true;

  try {
    await UsuarioService.liberarAcao({ ...form, ProcessoId: processoId });

    successMessage.value = 'Processo liberado! Você tem 3 minutos para executar a ação.';
    showToast('Processo liberado por 3 minutos.', { title: 'Liberado', type: 'success' });

    handleClose()
  } catch (error) {
    if (error.response?.data) {
      errorMessage.value = typeof error.response.data === 'string'
        ? error.response.data
        : error.response.data.message ?? 'Erro ao validar as credenciais.';
    } else {
      errorMessage.value = 'Não foi possível conectar ao servidor.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.authentication-card {
  max-width: 480px;
  margin: auto;
}

.form-control:focus {
  background-color: var(--surface-field);
  border-color: #198754;
  box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.1);
}
</style>
